/**
 * Web iframe transport + readiness lifecycle.
 *
 * Two intertwined concerns:
 *
 * 1. **Transport**: detect iframe context, exchange HANDSHAKE_REQUEST /
 *    HANDSHAKE_SUCCESS with the host, route subsequent
 *    postMessageToNativeApp messages over parent.postMessage. Mobile
 *    transports in post-message.ts are completely untouched.
 *
 * 2. **Readiness (1.3.0)**: expose a `ready` Promise + `onReady(cb)`
 *    so mini-app code can wait until the bridge is either confirmed
 *    available or confirmed not (timeout, standalone). Mobile resolves
 *    synchronously (microtask) — the native bridge is up before any JS
 *    runs. Web resolves on HANDSHAKE_SUCCESS or after a 5 s timeout if
 *    the host never replies (timeout is treated as "no bridge", NOT a
 *    rejection — `isWebViewBridgeAvailable()` afterwards returns false
 *    so mini-app gating code falls cleanly into the standalone branch).
 *
 * The companion in `post-message.ts` uses `isReadySettled()` to queue
 * method calls that arrive before ready settles, then drains them via
 * `onReady()`.
 */

import {hasAndroidPostMessage, hasWebKitPostMessage, isInIframe} from './detection';

const BRIDGE = '__orta_webview_bridge';

// ── Handshake state (web iframe path) ──────────────────────────────
let hostOrigin: string | null = null;
let capabilities: ReadonlyArray<string> = [];
let handshakeAttempted = false;

// ── Readiness lifecycle ────────────────────────────────────────────
let readySettled = false;
let resolveReadyPromise!: () => void;
const readyCallbacks: Array<() => void> = [];

/**
 * Resolves when the bridge state is determined — either:
 *   • mobile WebView injection detected synchronously (resolved this
 *     microtask)
 *   • we're NOT in an iframe (standalone — resolved this microtask)
 *   • HANDSHAKE_SUCCESS arrived from the host
 *   • the handshake timeout elapsed (still resolves; the bridge is
 *     simply unavailable — check `isWebViewBridgeAvailable()` after)
 *
 * Never rejects. Callers that need to branch on availability should
 * check `isWebViewBridgeAvailable()` AFTER awaiting `ready`.
 */
export const ready: Promise<void> = new Promise<void>((res) => {
    resolveReadyPromise = res;
});

/** Synchronous getter — true once `ready` has resolved. Used by the
 *  method queue in post-message.ts to decide between "queue" and
 *  "reject immediately". */
export const isReadySettled = (): boolean => readySettled;

/**
 * Subscribe to the ready event. Fires once. Returns an unsubscribe
 * function (no-op if already settled — the callback is fired in a
 * microtask).
 */
export const onReady = (cb: () => void): (() => void) => {
    if (readySettled) {
        Promise.resolve().then(cb);
        return () => {};
    }
    readyCallbacks.push(cb);
    return () => {
        const idx = readyCallbacks.indexOf(cb);
        if (idx !== -1) readyCallbacks.splice(idx, 1);
    };
};

function settleReady(): void {
    if (readySettled) return;
    readySettled = true;
    resolveReadyPromise();
    const callbacks = readyCallbacks.splice(0);
    callbacks.forEach((cb) => {
        try {
            cb();
        } catch (err) {
            // Don't let one bad subscriber take down the rest.
            // eslint-disable-next-line no-console
            console.error('[orta-bridge] onReady callback threw:', err);
        }
    });
}

// ── Public API (transport-side) ────────────────────────────────────
export const isWebIframeBridgeAvailable = (): boolean => hostOrigin !== null;
export const getWebIframeCapabilities = (): ReadonlyArray<string> => capabilities;

export const getWebIframePostMessage = (): ((jsonMessage: string) => void) | null => {
    if (!hostOrigin) return null;
    const origin = hostOrigin;
    return (jsonMessage: string) => {
        let parsed: unknown;
        try {
            parsed = JSON.parse(jsonMessage);
        } catch {
            return;
        }
        (window as any).parent.postMessage(parsed, origin);
    };
};

// ── Bootstrap ──────────────────────────────────────────────────────
//
// Settle ready immediately for cases that can be decided synchronously
// (mobile / standalone). For the iframe case, install the handshake
// listener + start a 5 s timeout so the queue can drain SOMEHOW even
// if the host never replies.

const HANDSHAKE_TIMEOUT_MS = 5000;

function bootstrap(): void {
    if (typeof (window as any) === 'undefined') {
        // SSR / Node — nothing to do; settle immediately so any consumer
        // that imported the SDK in a non-browser context doesn't hang.
        settleReady();
        return;
    }

    // Mobile WebView — globals are injected before any JS runs, so
    // detection is sync. Bridge is already usable.
    if (hasAndroidPostMessage() || hasWebKitPostMessage()) {
        settleReady();
        return;
    }

    // Not in iframe — standalone browser, no host to handshake with.
    // Bridge is permanently unavailable for this page lifetime.
    if (!isInIframe()) {
        settleReady();
        return;
    }

    // Web iframe path. Install the message listener + post
    // HANDSHAKE_REQUEST; on success or timeout, settle ready.
    installHandshake();

    setTimeout(() => {
        if (!readySettled) {
            // eslint-disable-next-line no-console
            console.warn(
                '[orta-bridge] handshake timed out after',
                HANDSHAKE_TIMEOUT_MS,
                'ms — bridge will report unavailable. Check that the iframe is loaded inside an orta host that emits HANDSHAKE_SUCCESS.',
            );
            settleReady();
        }
    }, HANDSHAKE_TIMEOUT_MS);
}

function installHandshake(): void {
    if (handshakeAttempted) return;
    handshakeAttempted = true;

    (window as any).addEventListener('message', handleParentMessage);

    // '*' is intentional — see header comment. Host filters on
    // event.origin for the actual defence.
    try {
        (window as any).parent.postMessage({type: 'HANDSHAKE_REQUEST'}, '*');
    } catch {
        // No parent / sandboxed restrictively — leave handshake unsent;
        // the timeout above will settle ready as "no bridge".
    }
}

function handleParentMessage(event: MessageEvent): void {
    if (event.source !== (window as any).parent) return;
    const msg: any = event.data;
    if (!msg || typeof msg !== 'object') return;

    // ── Phase 1: handshake ────────────────────────────────────────
    if (hostOrigin === null) {
        if (msg.type === 'HANDSHAKE_SUCCESS') {
            const payloadOrigin =
                msg.payload && typeof msg.payload.origin === 'string'
                    ? msg.payload.origin
                    : event.origin;
            hostOrigin = payloadOrigin;
            capabilities = Array.isArray(msg.payload?.capabilities)
                ? (msg.payload.capabilities as string[]).slice()
                : [];
            settleReady();
            return;
        }
        if (msg.type === 'HANDSHAKE_REJECTED') {
            // eslint-disable-next-line no-console
            console.warn('[orta-bridge] HANDSHAKE_REJECTED:', msg.payload);
            // Settle ready as unavailable — methods will reject going
            // forward (rather than hanging in the queue until timeout).
            settleReady();
            return;
        }
        return;
    }

    // ── Phase 2: protocol messages (responses + native events) ────
    if (event.origin !== hostOrigin) return;

    let jsonMessage: string;
    try {
        jsonMessage = JSON.stringify(msg);
    } catch {
        return;
    }
    const w = window as any;
    if (w[BRIDGE] && typeof w[BRIDGE].postMessage === 'function') {
        w[BRIDGE].postMessage(jsonMessage);
    }
}

bootstrap();
